import { Link } from "react-router-dom";
import { useGlobalContext } from "../context/GlobalContext";
import { useState, useRef, useEffect } from "react";
import requestHandler from "../utils/requestHandler.js";
import Tooltip from "./Tooltip.jsx";
import {
  DownloadIcon,
  NotebookIcon,
  BookBlankIcon,
  PlayIcon,
  PauseIcon,
} from "../assets/icons/index.js";

/**
 * 🎼 The Utility Maestro — Lesson Footer Command Center
 *
 * Conducts the grand orchestra of lesson utilities: downloads, notes, highlights,
 * and font sizes. The GlobalMusicBar now floats at bottom-[75px] per ROS-203,
 * well above this 64px-tall sticky bar — no offset gymnastics needed. 🤸‍♂️✨
 */
const UtilityPanel = ({ notesPanelOpen, setNotesPanelOpen, lessonContent }) => {
  const { highlightColor, setHighlightColor, fontSize, setFontSize } =
    useGlobalContext();
  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = useRef(null);

  const audioUrl = lessonContent?.mp3;

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const onEnded = () => setIsPlaying(false);
    audio.addEventListener("ended", onEnded);
    return () => audio.removeEventListener("ended", onEnded);
  }, []);

  const togglePlay = () => {
    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      audioRef.current
        .play()
        .then(() => setIsPlaying(true))
        .catch((error) => console.error("Playback failed:", error));
    }
  };

  const handlePDFDownload = async (lessonId) => {
    const res = await requestHandler({
      path: `lessons/${lessonId}/pdf`,
      method: "GET",
    });

    if (res.success) {
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank", "noopener,noreferrer");
    } else {
      console.error("Error fetching PDF:", res.error);
    }
  };

  return (
    <>
      {/* Utility Panel */}
      <div className="flex items-center px-2 lg:px-8 h-16 bg-lz-ink-mid sticky bottom-0 -mt-3 z-40">
        <Link
          to={`/degree/${lessonContent?.current_degree_slug || "neophyte"}`}
        >
          <div className="flex items-center gap-4 flex-1">
            <BookBlankIcon className="ml-2 h-5 w-5 fill-lz-concrete hover:cursor-pointer" />
            <p className="text-lz-concrete text-sm md:text-base">
              {lessonContent?.current_degree_name}
            </p>
          </div>
        </Link>
        <div className="hidden lg:flex items-center absolute left-1/2 transform -translate-x-1/2">
          <div
            className="flex items-center border-r h-3 border-gray-400 ml-4 text-lz-concrete"
            data-tooltip-id="utility-font-size"
            data-tooltip-content="Adjust text size"
          >
            <span
              className={`text-lg mr-4 px-2 hover:cursor-pointer ${
                fontSize === "sm" ? "bg-lz-candle-dim rounded-lg" : ""
              }`}
              onClick={() => setFontSize("sm")}
            >
              A
            </span>
            <span
              className={`text-2xl mr-4 px-2 hover:cursor-pointer ${
                fontSize === "md" ? "bg-lz-candle-dim rounded-lg" : ""
              }`}
              onClick={() => setFontSize("md")}
            >
              A
            </span>
            <span
              className={`text-4xl mr-4 px-2 hover:cursor-pointer ${
                fontSize === "lg" ? "bg-lz-candle-dim rounded-lg" : ""
              }`}
              onClick={() => setFontSize("lg")}
            >
              A
            </span>
          </div>
          <div
            className="flex items-center ml-4 border-r h-3 border-gray-400 text-lz-concrete"
            data-tooltip-id="utility-highlight"
            data-tooltip-content="Highlight text"
          >
            <div
              id="0"
              className={`flex justify-center h-8 w-8 rounded-md cursor-pointer ${
                highlightColor === "yellow" ? "bg-lz-candle-dim" : ""
              }`}
              onClick={() => setHighlightColor("yellow")}
            >
              <div className="h-6 w-6 m-auto bg-lz-yellow rounded-full" />
            </div>
            <div
              id="1"
              className={`flex justify-center h-8 w-8 rounded-md cursor-pointer ${
                highlightColor === "green" ? "bg-lz-candle-dim" : ""
              }`}
              onClick={() => setHighlightColor("green")}
            >
              <div className="h-6 w-6 m-auto bg-lz-light-green rounded-full" />
            </div>
            <div
              id="2"
              className={`flex justify-center h-8 w-8 mr-4 rounded-md cursor-pointer ${
                highlightColor === "pink" ? "bg-lz-candle-dim" : ""
              }`}
              onClick={() => setHighlightColor("pink")}
            >
              <div className="h-6 w-6 m-auto bg-lz-light-pink rounded-full" />
            </div>
          </div>
          <div className="flex items-center ml-4 text-lz-concrete">
            <button
              className="flex justify-center cursor-pointer"
              onClick={() => setNotesPanelOpen(!notesPanelOpen)}
              aria-label="Toggle notes panel"
              data-tooltip-id="utility-notes"
              data-tooltip-content="View notes"
            >
              <NotebookIcon className="ml-2 h-7 w-7 fill-lz-concrete hover:cursor-pointer" />
            </button>
          </div>
        </div>

        <div className="flex gap-4 flex-1 justify-end items-center">
          {audioUrl && (
            <div className="hidden lg:flex items-center">
              <audio ref={audioRef} src={audioUrl} />
              <button
                onClick={togglePlay}
                className="focus:outline-none"
                aria-label={isPlaying ? "Pause audio" : "Play audio"}
              >
                {isPlaying ? (
                  <PauseIcon className="h-6 w-6 fill-lz-concrete hover:cursor-pointer" />
                ) : (
                  <PlayIcon className="h-6 w-6 fill-lz-concrete hover:cursor-pointer" />
                )}
              </button>
            </div>
          )}

          <p className="text-lz-concrete text-sm md:text-base">
            {lessonContent?.footer_title}
          </p>

          {lessonContent?.pdf && (
            <button
              onClick={() => handlePDFDownload(lessonContent?.id)}
              aria-label="Download PDF"
              data-tooltip-id="utility-pdf-download"
              data-tooltip-content="Download PDF"
            >
              <DownloadIcon className="ml-2 h-6 w-6 fill-lz-concrete hover:cursor-pointer" />
            </button>
          )}
        </div>
      </div>
      <Tooltip id="utility-font-size" ariaLabel="Adjust text size" />
      <Tooltip id="utility-highlight" ariaLabel="Highlight text" />
      <Tooltip id="utility-notes" ariaLabel="View notes" />
      <Tooltip id="utility-pdf-download" ariaLabel="Download PDF" />
    </>
  );
};

export default UtilityPanel;
